import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Spinner,
} from "react-bootstrap";
import Swal from "sweetalert2";
import { useForm } from "react-hook-form";
import CompanySelector from "ATSComponents/Common/CompanySelector";
import OrgTableShimmer from "ATSComponents/Common/OrgTableShimmer";
import { ArrowLeft, Bot, Building2, FileText, MessageSquareText, Mic2, Save } from "lucide-react";

const AgentCreation = () => {
  document.title = "ATS - Agent Creation";

  const navigate = useNavigate();

  let firstmessage =
    "Hey! This is {agent_name} from {company_name}, I'm the AI Recruiter here. I saw your application for the {designation} position. Is this a good time for a quick 5-minute conversation?";

  let first_prompt = `ROLE AND IDENTITY:
            You are {interviewer_name}, a {interviewer_designation} at {company_name} responsible for conducting interviews for the {domain} team. Your primary objective is to take initial screening of the candidate.

            **COMPANY OVERVIEW**:
            - Company: {company_name}
            - Website: {company_website}
            - About Company: {about_company}

            **CANDIDATE DETAILS**:
            - First Name: {first_name}
            - Last Name: {last_name}
            - Total experiece: {total_experience}
            - Email: {email}
            - Phone: {phone}
            - Education History: {education_history}
            - Employment History: {employment_history}
            - Skills: {skills}
            - Certifications: {certifications}

            **JOB DESCRIPTION**:
            -"role applied for": {designation},
            -"minimum_experience":{minimum_experience},
            -"maximum_experience":{maximum_experience},
            -"primary_skills": {primary_skills},
            -"domain":{domain}


            **INTERVIEW SEGMENTS**:
            Ask these details from the candidate:
            {questions_to_ask}

            **OBJECTION/RESPONSE HANDLING**:
            - For Nervousness: Create supportive environment

            **CONVERSATIONAL INTERJECTIONS**:
            1. Understanding Sounds:
            "I see..."
            "Got it!"
            "Hmm, tell me more..."
            "That makes sense."

            2. Encouraging Phrases:
            "Walk me through your thinking..."
            "How did you approach that?"
            "What was your reasoning?"
            "Can you elaborate on that?"

            3. Reflective Moments:
            "Let's unpack that..."
            "So, basically..."
            "Just to clarify..."
            "Okay, here's what I'm hearing..."

            4. Transition Phrases:
            "Moving on to..."
            "I'd like to explore..."
            "Let's dive into..."
            "Next, I'm curious about..."

            **CRITICAL INTERVIEW RULES**:
            - Maintain objectivity
            - Never make assumptions
            - Respect candidate's privacy
            - Avoid discriminatory language
            - Focus on skills and potential
            - Provide constructive feedback
            - Maintain professional confidentiality

            **DOCUMENTATION PROTOCOL**:
            - Capture detailed interview notes
            - Rate candidates on predefined criteria
            - Document specific strengths and potential concerns
            - Prepare comprehensive interview report
            - Recommend next steps

            **POST-INTERVIEW PROCESS**:
            - Communicate next steps to candidate

            **ERROR HANDLING**:
            - For Unclear Responses: "Could you clarify that?"
            - For Technical Difficulties: "Shall we reconnect?"
            - For Unexpected Scenarios: Remain calm, professional
            - If Candidate Struggles: Provide gentle guidance

            **COMMUNICATION GUIDELINES**:
            1. Communication Style:
            - Be Professional yet Conversational
            - Create a comfortable interview environment
            - Strictly avoid robotic or overly formal language
            - Keep responses precise and purposeful
            - Adapt tone based on candidate's communication style
            - Maintain empathetic and engaging interaction
            -TONE CALIBRATION:
            - 40% Professional
            - 30% Analytical
            - 20% Encouraging
            - 10% Subtle Warmth

            2. Conversation Flow:
            - Start with a professional greeting and Verify if speaking with the correct person mentioned in resume like "Hello! I'm your_name from our_company_name.Am i speaking with candidate_name?"
            - After verifying the name, if he is the correct person mentioned in resume say "Great! We have reviewed your profile, and we'd like to move forward with the next step in the hiring process."
            - After that say "we would like to conduct a quick telephonic interview now and this will be a short discussion, we'll ask basic details like experience,salary,notice period..."
            - Verify whether if it's correct time and they want to continue with the initial telephonic interview or if they prefer some other time.
            - Strictly follow **INTERVIEW FLOW SEGMENTS** for other sections
            - Strictly don't extend the answer by repeating or emphasizing the user response,simply ask next question directly after the user responds
            - Very Strictly don't ask follow up questions
            - Very Strictly take confirmation of all the fields once they provide all the answers.
            - After taking confirmation of all the fields, Ask candidate if have any questions and resolve basic queries and for other queries tell them to visit our website or contact to company email.
            - If asked about salary details, handle conversation without mentioning particular figures
            - If wrong person or the conversation has ended, end the call professionally

            **OVERALL GUIDELINES**:
            - Show genuine interest
            - Ask thought-provoking questions
            - Provide subtle company insights
            - Make the candidate feel valued
            - Maintain high engagement throughout the interview
            - Generate responses that feel natural and human
            - Generate responses not greater than 20 words
            - Strictly follow and start with Conversation Flow provided
            - Strictly don't extend the answer by repeating or emphasizing the user response,simply ask next question directly after the user responds
            - Very Strictly don't ask follow up questions
            - Very Strictly total questions including all the section should not be more than 10
`;

  let second_prompt = `ROLE AND IDENTITY:
            You are {interviewer_name}, a {interviewer_designation} at {company_name} responsible for conducting interviews for the team. Your primary objective is to take secondary technical screening of the candidate.

            **COMPANY OVERVIEW**:
            - Company: {company_name}
            - Website: {company_website}
            - About Company: {about_company}

            **CANDIDATE DETAILS**:
            - First Name: {first_name}
            - Last Name: {last_name}
            - Total experiece: {total_experience}
            - Email: {email}
            - Phone: {phone}
            - Education History: {education_history}
            - Employment History: {employment_history}
            - Skills: {skills}
            - Certifications: {certifications}

            **JOB DESCRIPTION**:
            -"role applied for": {designation},
            -"minimum_experience":{minimum_experience},
            -"maximum_experience":{maximum_experience},
            -"primary_skills": {primary_skills},
            -"domain":{domain}

            **INTERVIEW OBJECTIVES**:
            1. Core Assessment Goals:
            - Evaluate technical proficiency
            - Assess cultural fit

            **INTERVIEW SEGMENTS**:
            Based on Job Description, Develop Questions in Following Categories:
            {questions_to_ask}

            **OBJECTION/RESPONSE HANDLING**:
            - For Challenging Questions: Encourage thinking process
            - For Nervousness: Create supportive environment

            **CONVERSATIONAL INTERJECTIONS**:
            1. Understanding Sounds:
            "I see..."
            "Got it!"
            "Hmm, tell me more..."
            "That makes sense."

            2. Encouraging Phrases:
            "Walk me through your thinking..."
            "How did you approach that?"
            "What was your reasoning?"
            "Can you elaborate on that?"

            3. Reflective Moments:
            "Let's unpack that..."
            "So, basically..."
            "Just to clarify..."
            "Okay, here's what I'm hearing..."

            4. Transition Phrases:
            "Moving on to..."
            "I'd like to explore..."
            "Let's dive into..."
            "Next, I'm curious about..."

            **CRITICAL INTERVIEW RULES**:
            - Maintain objectivity
            - Never make assumptions
            - Respect candidate's privacy
            - Avoid discriminatory language
            - Focus on skills and potential
            - Provide constructive feedback
            - Maintain professional confidentiality

            **DOCUMENTATION PROTOCOL**:
            - Capture detailed interview notes
            - Rate candidates on predefined criteria
            - Document specific strengths and potential concerns
            - Prepare comprehensive interview report
            - Recommend next steps

            **POST-INTERVIEW PROCESS**:
            - Summarize candidate's performance
            - Rate against job requirements
            - Prepare recommendation
            - Communicate next steps to candidate
            - Update hiring team

            **ERROR HANDLING**:
            - For Unclear Responses: "Could you clarify that?"
            - For Technical Difficulties: "Shall we reconnect?"
            - For Unexpected Scenarios: Remain calm, professional
            - If Candidate Struggles: Provide gentle guidance

            **COMMUNICATION GUIDELINES**:
            1. Communication Style:
            - Be Professional yet Conversational
            - Create a comfortable interview environment
            - Strictly avoid robotic or overly formal language
            - Keep responses precise and purposeful
            - Adapt tone based on candidate's communication style
            - Maintain empathetic and engaging interaction
            -TONE CALIBRATION:
            - 40% Professional
            - 30% Analytical
            - 20% Encouraging
            - 10% Subtle Warmth

            2. Conversation Flow:
            - Start with a professional greeting and Verify if speaking with the correct person mentioned in resume like "Hello! I'm your_name from our_company_name.Am i speaking with candidate_name?"
            - After verifying the name, if he is the correct person mentioned in resume say "Great! Congratulations candidate_name! You have been shortlisted for the position_name at our_company_name."
            - Verify whether if it's correct time and they want to continue with the interview
            - Briefly introduce company
            - Strictly follow **INTERVIEW FLOW SEGMENTS** for other sections
            - Strictly don't extend the answer by repeating or emphasizing the user response,simply ask next question directly after the user responds
            - Very Strictly don't ask follow up questions
            - Ask candidate if he has any questions and resolve basic queries and for other questions tell them to visit our website or contact to company email.
            - If wrong person or the conversation has ended, end the call professionally

            **ENGAGEMENT ENHANCEMENT**:
            - Show genuine interest
            - Ask thought-provoking questions
            - Provide subtle company insights
            - Make the candidate feel valued
            - Maintain high engagement throughout the interview
            - Generate responses that feel natural and human
            - Strictly follow and start with Conversation Flow provided
            - Strictly don't extend the answer by repeating or emphasizing the user response,simply ask next question directly after the user responds
            - Very Strictly don't ask follow up questions
            - Very Strictly total questions including all the section should not be more than 10
        `;

  let url = new URL(window.location.href);
  let params = new URLSearchParams(url.search.slice(1));
  let obj = {};
  for (let pair of params.entries()) {
    obj[pair[0]] = pair[1];
  }
  const agentId = obj["id"];

  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(!!agentId);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [agentVoiceCode, setAgentVoiceCode] = useState("");
  const [companyId, setCompanyId] = useState(null);
  const handleCompanyChange = (c) => {
    if (c?.company_id) {
      setCompanyId(c.company_id);
    }
  };

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      bot_name: "",
      agent_company_name: "",
      agent_company_website: "",
      about_the_company: "",
      meeting_duration: "",
      first_message: firstmessage,
      second_message: firstmessage,
      first_prompt: first_prompt,
      second_prompt: second_prompt,
      voice: "",
    },
  });

  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    Getagentvoice();
    if (agentId) {
      getSingleAgentById(agentId);
    }
  }, []);

  useEffect(() => {
    if (voices.length === 0 || !agentVoiceCode) return;
    const voiceObj = voices.find((v) => v.voice_code === agentVoiceCode);
    if (voiceObj) {
      setSelectedVoice(voiceObj);
      setValue("voice", voiceObj.name);
      if (voiceObj.audio_file) {
        setAudioUrl("https://callingagent.pranathiss.com" + voiceObj.audio_file);
      }
    }
  }, [voices, agentVoiceCode]);

  const Getagentvoice = async () => {
    try {
      const response = await axios.get("/api/v1/voices/");
      if (response.data) {
        setVoices(response.data);
      }
    } catch (error) {
      console.error("Error fetching voices:", error);
    }
  };

  const getSingleAgentById = async (id) => {
    try {
      const response = await axios.get(`/api/v1/agents/?agent_id=${id}`);
      const data = response.data?.data || response.data;
      // console.info("Agent API response:", data);
      if (data) {
        reset({
          bot_name: data.bot_name || "",
          agent_company_name: data.agent_company_name || "",
          agent_company_website: data.agent_company_website || "",
          about_the_company: data.about_the_company || "",
          meeting_duration: data.meeting_duration || "",
          first_message: data.first_message || firstmessage,
          first_prompt: data.first_prompt || first_prompt,
          second_message: data.second_message || firstmessage,
          second_prompt: data.second_prompt || second_prompt,
          voice: data.voice_name || "",
        });
        if (data.voice_code) {
          setAgentVoiceCode(data.voice_code);
        }
      }
    } catch (error) {
      console.error("Error fetching agent data:", error);
    } finally {
      setDataLoading(false);
    }
  };

  const handleSelectChange = (event) => {
    const selectedName = event.target.value;
    const voiceObj = voices.find((voice) => voice.name === selectedName);
    if (voiceObj) {
      setSelectedVoice(voiceObj);
      setAudioUrl("https://callingagent.pranathiss.com" + voiceObj.audio_file);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    const payload = {
      bot_name: data.bot_name,
      agent_company_name: data.agent_company_name,
      agent_company_website: data.agent_company_website,
      about_the_company: data.about_the_company,
      meeting_duration: data.meeting_duration,
      voice_code: selectedVoice?.voice_code || "",
      language_code: selectedVoice?.language_code || "",
      voice_name: selectedVoice?.name || "",
      company_id: companyId,
      first_message: data.first_message,
      first_prompt: data.first_prompt,
      second_message: data.second_message,
      second_prompt: data.second_prompt,
    };

    try {
      if (agentId) {
        const response = await axios.patch(`/api/v1/agents/?agent_id=${agentId}`, payload);
        if (response.status === 200) {
          Swal.fire({
              icon: "success",
              title: "Success",
              text: "Agent Updated Successfully",
              timer: 2000,
              showConfirmButton: false,
            });
          navigate("/agentList");
        }
      } else {
        const response = await axios.post("/api/v1/agents/", payload);
        if (response.status === 201 || response.status === 200) {
          Swal.fire({
              icon: "success",
              title: "Success",
              text: "Agent Created Successfully",
              timer: 2000,
              showConfirmButton: false,
            });

          navigate("/agentList");
        }
      }
    } catch (error) {
      console.error("Agent save failed:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message || "Something went wrong",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  if (dataLoading) {
    return (
      <div className="page-content allact-tabs">
        <Container fluid>
          <OrgTableShimmer columns={4} rows={8} titleWidth="220px" />
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content allact-tabs agent-page">
      <Container fluid>
        <div className="agent-create-hero">
          <div className="agent-list-title">
            <div className="agent-list-hero-icon">
              <Bot size={22} />
            </div>
            <div>
              <h1>Agent {agentId ? "Edit" : "Creation"}</h1>
              <p>Configure the calling agent, screening messages, prompts, voice, and call duration.</p>
            </div>
          </div>
          <div className="agent-create-hero-actions">
            <div className="agent-create-company">
              <label>Select Company</label>
              <CompanySelector onCompanyChange={handleCompanyChange} />
            </div>
            <Button
              variant="outline-light"
              className="agent-create-back-btn"
              disabled={loading}
              onClick={() => !loading && navigate("/agentList")}
            >
              <ArrowLeft size={16} /> Back
            </Button>
          </div>
        </div>

        <Form onSubmit={handleSubmit(onSubmit)} className="agent-form-shell">
          <div className="agent-form-section">
            <div className="agent-form-section-title">
              <Building2 size={17} />
              <span>Agent & Company Details</span>
            </div>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Agent Name *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter agent name"
                    isInvalid={!!errors.bot_name}
                    {...register("bot_name", { required: "Agent name is required" })}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.bot_name?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Company Website *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="https://example.com"
                    isInvalid={!!errors.agent_company_website}
                    {...register("agent_company_website", {
                      required: "Company website is required",
                      pattern: {
                        value: /^(https?:\/\/)([\w\d-]+\.)+\w{2,}(\/.*)?\/?$/,
                        message: "Enter a valid URL starting with http:// or https://",
                      },
                    })}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.agent_company_website?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Company Name *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter company name"
                    isInvalid={!!errors.agent_company_name}
                    {...register("agent_company_name", {
                      required: "Company name is required",
                    })}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.agent_company_name?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </div>

          <div className="agent-form-section">
            <div className="agent-form-section-title">
              <MessageSquareText size={17} />
              <span>Screening Messages</span>
            </div>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>First Message (Screening 1) *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter first message"
                    isInvalid={!!errors.first_message}
                    {...register("first_message", {
                      required: "First message is required",
                    })}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.first_message?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>First Message (Screening 2) *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter second message"
                    isInvalid={!!errors.second_message}
                    {...register("second_message", {
                      required: "Second message is required",
                    })}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.second_message?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </div>

          <div className="agent-form-section">
            <div className="agent-form-section-title">
              <FileText size={17} />
              <span>Screening Templates</span>
            </div>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Template (Screening 1) *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={9}
                    className="agent-template-textarea"
                    isInvalid={!!errors.first_prompt}
                    {...register("first_prompt", {
                      required: "Template is required",
                    })}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.first_prompt?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Template (Screening 2) *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={9}
                    className="agent-template-textarea"
                    isInvalid={!!errors.second_prompt}
                    {...register("second_prompt", {
                      required: "Template is required",
                    })}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.second_prompt?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </div>

          <div className="agent-form-section">
            <div className="agent-form-section-title">
              <Mic2 size={17} />
              <span>Voice & Call Settings</span>
            </div>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Describe About Company *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Enter company description"
                    isInvalid={!!errors.about_the_company}
                    {...register("about_the_company", {
                      required: "Description is required",
                    })}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.about_the_company?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Call Duration *</Form.Label>
                  <Form.Select
                    isInvalid={!!errors.meeting_duration}
                    {...register("meeting_duration", {
                      required: "Please select call timing",
                    })}
                  >
                    <option value="">Select Duration</option>
                    <option value="00:10:00">00:10:00 Min</option>
                    <option value="00:15:00">00:15:00 Min</option>
                    <option value="00:20:00">00:20:00 Min</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.meeting_duration?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Voice *</Form.Label>
                  <Form.Select
                    isInvalid={!!errors.voice}
                    {...register("voice", {
                      onChange: handleSelectChange,
                      required: "Please select a voice",
                    })}
                  >
                    <option value="" disabled>
                      Select Voice
                    </option>
                    {voices.map((voice) => (
                      <option key={voice.name} value={voice.name}>
                        {voice.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.voice?.message}
                  </Form.Control.Feedback>

                  {selectedVoice && audioUrl && (
                    <audio
                      controls
                      src={audioUrl}
                      type="audio/wav"
                      className="agent-voice-preview"
                    />
                  )}
                </Form.Group>
              </Col>
            </Row>
          </div>

          <div className="agent-form-actions">
            <Button
              variant="light"
              type="button"
              disabled={loading}
              onClick={() => !loading && navigate("/agentList")}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" /> Saving...
                </>
              ) : (
                <>
                  <Save size={16} /> Save
                </>
              )}
            </Button>
          </div>
        </Form>
      </Container>
    </div>
  );
};

export default AgentCreation;
